<template>
  <div class="user-info-container">
    <div class="user-info-wrapper">
      <!-- Header Section -->
      <div class="user-info-header">
        <div class="header-content">
          <div class="avatar-section">
            <div class="avatar-wrapper">
              <img v-if="avatar" :src="getAvatarUrl(avatar)" alt="Avatar" class="user-avatar" loading="lazy" @error="handleAvatarError" />
              <div v-else class="avatar-placeholder">
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
              </div>
            </div>
            <div class="user-info-text">
              <h1 class="user-name">{{ username || 'User' }}</h1>
              <p class="user-email">{{ email || 'user@example.com' }}</p>
            </div>
          </div>
          <p class="page-description">{{ $t('pages.userInfo.manage_your_profile_settings_updat_6a6cb13') }}</p>
        </div>
      </div>

      <!-- Profile Section -->
      <div class="settings-section">
        <div class="section-header" @click="toggleOption('avatar')">
          <div class="section-title-group">
            <div class="section-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </div>
            <h2 class="section-title">{{ $t('pages.userInfo.profile_settings_df4b081') }}</h2>
          </div>
          <svg class="chevron-icon" :class="{ rotated: isAvatarOpen }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        <transition name="slide-down">
          <div v-if="isAvatarOpen" class="section-content">
            <!-- Gender Selection -->
            <div class="form-section">
              <label class="form-label">{{ $t('pages.userInfo.gender_8a754c6') }}</label>
              <div class="gender-buttons">
                <button
                  class="gender-btn"
                  :class="{ active: selectedGender === 'male' }"
                  @click="selectGender('male')"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="10" cy="14" r="7"></circle>
                    <path d="M21 10l-3-3m0 6l3-3"></path>
                  </svg>
                  {{ $t('pages.userInfo.male_3f3a489') }}
                </button>
                <button
                  class="gender-btn"
                  :class="{ active: selectedGender === 'female' }"
                  @click="selectGender('female')"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="8" r="5"></circle>
                    <path d="M12 13v6"></path>
                    <path d="M9 18h6"></path>
                  </svg>
                  {{ $t('pages.userInfo.female_b7c17e9') }}
                </button>
                <button
                  class="gender-btn"
                  :class="{ active: selectedGender === 'other' }"
                  @click="selectGender('other')"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="9"></circle>
                  </svg>
                  {{ $t('pages.userInfo.other_6e6a6f2') }}
                </button>
              </div>
            </div>

            <!-- Academic Year -->
            <div class="form-section">
              <label class="form-label">{{ $t('pages.userInfo.academic_year_5f1566b') }} <span class="year-display">{{ labels[selectedYear] }}</span></label>
              <div class="slider-container">
                <input 
                  type="range" 
                  id="academic-year" 
                  min="0" 
                  max="5" 
                  v-model.number="selectedYear" 
                  @input="updateValue"
                  class="year-slider"
                />
                <div class="slider-labels">
                  <span v-for="(label, index) in labels" :key="index" class="slider-label" :class="{ active: selectedYear === index }">
                    {{ label }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Avatar Selection -->
            <div class="form-section">
              <label class="form-label">{{ $t('pages.userInfo.choose_avatar_98122fb') }}</label>
              <div class="avatar-grid">
                <div
                  v-for="item in Avatars"
                  :key="item.id"
                  class="avatar-option"
                  :class="{ selected: isAvatarSelected(item) }"
                  @click="selectAvatar(item.imgUrl)"
                >
                  <img :src="item.imgUrl" :alt="'Avatar ' + item.name" loading="lazy" />
                  <div class="avatar-check">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3">
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            <button class="save-btn" @click="updateAvatar">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              {{ $t('pages.userInfo.save_profile_changes_24dcd52') }}
            </button>
          </div>
        </transition>
      </div>

      <!-- Credentials Section -->
      <div class="settings-section">
        <div class="section-header" @click="toggleOption('credentials')">
          <div class="section-title-group">
            <div class="section-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
              </svg>
            </div>
            <h2 class="section-title">{{ $t('pages.userInfo.account_credentials_481ad68') }}</h2>
          </div>
          <svg class="chevron-icon" :class="{ rotated: isCredentialsOpen }" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>

        <transition name="slide-down">
          <div v-if="isCredentialsOpen" class="section-content">
            <div class="credentials-grid">
              <div class="form-group">
                <label class="form-label">{{ $t('pages.userInfo.username_84c2901') }}</label>
                <div class="input-readonly">{{ username }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('pages.userInfo.email_84add5b') }}</label>
                <div class="input-readonly">{{ email }}</div>
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('pages.userInfo.current_password_4d59678') }}</label>
                <input 
                  type="password" 
                  v-model="password" 
                  :placeholder="$t('pages.userInfo.enter_your_current_password_9149cfc')"
                  class="form-input"
                />
              </div>
              <div class="form-group">
                <label class="form-label">{{ $t('pages.userInfo.new_password_4894cb3') }}</label>
                <input 
                  type="password" 
                  v-model="newPassword" 
                  :placeholder="$t('pages.userInfo.enter_your_new_password_7bec90c')"
                  class="form-input"
                />
              </div>
              <div class="form-group full-width">
                <label class="form-label">{{ $t('pages.userInfo.verification_code_50cdc18') }}</label>
                <div class="code-input-group">
                  <input 
                    type="text" 
                    v-model="code" 
                    :placeholder="$t('pages.userInfo.enter_6_digit_code_4c9c785')"
                    class="form-input"
                    maxlength="6"
                  />
                  <button class="send-code-btn" @click="sendCode">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                    {{ $t('pages.userInfo.send_code_f687607') }}
                  </button>
                </div>
              </div>
            </div>
            <button class="save-btn" @click="updateCredentials">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                <polyline points="17 21 17 13 7 13 7 21"></polyline>
                <polyline points="7 3 7 8 15 8"></polyline>
              </svg>
              {{ $t('pages.userInfo.update_credentials_f749a73') }}
            </button>
          </div>
        </transition>
      </div>

      <!-- University Section (read-only — auto-set from email domain) -->
      <div class="settings-section">
        <div class="section-header non-collapsing">
          <div class="section-title-group">
            <div class="section-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
              </svg>
            </div>
            <h2 class="section-title">{{ $t('pages.userInfo.university_715432d') }}</h2>
          </div>
        </div>
        <div class="section-content">
          <div class="form-section">
            <div class="input-readonly">{{ selectedUniversity || 'Not set' }}</div>
            <p class="hint-text" style="margin-top: 8px; color: #64748b; font-size: 0.875rem;">
              Determined automatically from your university email ({{ email || '—' }}). Contact us if this is wrong.
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';
  import Swal from 'sweetalert2';
  import { syncStoredAuthFromToken } from '@/utils/auth';
  export default {
    data() {
      return {
        isAvatarOpen: true,
        isCredentialsOpen: false,
        selectedGender: '',
        selectedYear: 0,
        selectedUniversity: '',
        selectedAvatar: '',
        labels: ['Freshmen', 'Sophomore', 'Junior', 'Senior', 'Master', 'PhD'],
        Houses:[],
        Avatars:[],
        username: '',
        email: '',
        avatar: '',
        password: '',
        newPassword: '',
        code: '',
      };
    },
    mounted() {
      this.loadUserInfo();
        this.userName = localStorage.getItem('name');
        this.fetchHouses();
        this.fetchAvatars();

    },
    methods: {
        fetchHouses() {
        axios.get('/api/info/university', { timeout: 10000 })
          .then(response => {
            console.log(response)
            this.Houses = response.data.data;
            console.log(this.Houses)
          })
          .catch(error => {
            console.error('Error fetching data:', error);
            this.houses = [];
          });
    },
    fetchAvatars() {
        axios.get('/api/info/avatars', { timeout: 10000 })
          .then(response => {
            console.log(response)
            this.Avatars = response.data.data;
            console.log(this.Avatars)
          })
          .catch(error => {
            console.error('Error fetching data:', error);
            this.Avatars = [];
          });
    },
        finish() {
        axios.post('/api/info/update', {
          "gender": this.selectedGender,
          "grade": this.selectedYear,
          "avatar": this.selectedAvatar
        }, { timeout: 10000 })
        .then(function (response) {
          console.log(response);
          const token = response?.data?.data;
          const payload = syncStoredAuthFromToken(token);
          if (!payload) {
            throw new Error('Profile update returned an invalid token');
          }

          Swal.fire({
            title: 'Success!',
            text: 'Profile updated successfully',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          }).then(() => {
            location.reload(true);
          });
        })
        .catch(function (error) {
          Swal.fire({
            title: 'Error',
            text: error.response?.data?.message || 'Failed to update profile',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
        });
    },
      toggleOption(option) {
        if (option === 'avatar') {
          this.isAvatarOpen = !this.isAvatarOpen;
          this.isCredentialsOpen = false;
        } else if (option === 'credentials') {
          this.isCredentialsOpen = !this.isCredentialsOpen;
          this.isAvatarOpen = false;
        }
      },
      selectAvatar(imgUrl) {
        this.selectedAvatar = imgUrl;
      },
      isAvatarSelected(item) {
        if (!this.selectedAvatar) return false;
        const sel = String(this.selectedAvatar).trim();
        const candidates = [item.imgUrl, item.name, '/avatar/' + String(item.name || '').replace(/\D+/g, '') + '.png'];
        return candidates.some(c => c && (sel === c || sel.endsWith(c) || (c.endsWith && c.endsWith(sel))));
      },
      selectGender(gender) {
        this.selectedGender = gender;
      },
      selectYear(year) {
        this.selectedYear = year;
      },
      updateValue() {
        // Slider value updated
      },
      updateAvatar() {
        this.finish();
      },
      sendCode() {
        console.log('Sending code...');
        axios.get('/api/user/sendcode', { timeout: 10000 })
        .then(function (response) {
          console.log(response);
          Swal.fire({
            title: 'Code Sent!',
            text: 'Verification code has been sent to your email',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
        })
        .catch(function (error) {
          console.log(error);
          Swal.fire({
            title: 'Error',
            text: 'Failed to send verification code',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
        });
      },
      verifyAccount() {
        console.log('Verifying account...');
        axios.get('/api/user/sendcode', { timeout: 10000 })
        .then(function (response) {
          console.log(response.data);
        })
        .catch(function (error) {
          console.log(error);
        });
      },
      updateCredentials() {
        console.log('Updating credentials...');
        console.log(this.email);
        console.log(this.password);
        axios.post('/api/user/changePassword', {
          "email": this.email,
          "oldPassword": this.password,
          "newPassword": this.newPassword,
          'token': this.code,
        }, { timeout: 10000 })
        .then(function (response) {
          console.log(response);
          Swal.fire({
            title: 'Success!',
            text: 'Credentials updated successfully',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
        })
        .catch(function (error) {
          console.log(error);
          Swal.fire({
            title: 'Error',
            text: error.response?.data?.message || 'Failed to update credentials',
            icon: 'error',
            confirmButtonText: 'OK',
            confirmButtonColor: '#3bc2ff'
          });
        });
      },
      loadUserInfo() {
        this.username = localStorage.getItem('name') || '';
        this.email = localStorage.getItem('email') || '';
        this.avatar = localStorage.getItem('avatar') || '';
        this.selectedUniversity = localStorage.getItem('school') || '';
        const gender = localStorage.getItem('gender');
        const grade = localStorage.getItem('grade');
        const avatar = localStorage.getItem('avatar');
        
        if (gender) this.selectedGender = gender;
        if (grade !== null && grade !== undefined && grade !== '') {
          // Backend stores grade as numeric index ("0".."5"); legacy data may store the label.
          const gradeNum = Number(grade);
          if (!Number.isNaN(gradeNum) && gradeNum >= 0 && gradeNum < this.labels.length) {
            this.selectedYear = gradeNum;
          } else {
            const gradeIndex = this.labels.indexOf(grade);
            if (gradeIndex !== -1) this.selectedYear = gradeIndex;
          }
        }
        if (avatar) {
          const legacyNamedAvatarMatch = avatar.match(/Avatar\s+(\d+)\.png$/i);
          this.selectedAvatar = legacyNamedAvatarMatch
            ? `/avatar/${legacyNamedAvatarMatch[1]}.png`
            : avatar;
        }
      },
      getAvatarUrl(avatar) {
        if (!avatar || avatar === '' || avatar === '/avatar/default-avatar.png') {
          return '/avatar/1.png';
        }
        const normalizedAvatar = String(avatar).trim();
        const legacyNamedAvatarMatch = normalizedAvatar.match(/Avatar\s+(\d+)\.png$/i);
        if (legacyNamedAvatarMatch) {
          return `/avatar/${legacyNamedAvatarMatch[1]}.png`;
        }
        if (normalizedAvatar.startsWith('http://') || normalizedAvatar.startsWith('https://')) {
          return normalizedAvatar;
        }
        if (normalizedAvatar.startsWith('/avatar/')) {
          return normalizedAvatar;
        }
        return '/avatar/' + normalizedAvatar.replace(/^\/+/, '');
      },
      handleAvatarError(event) {
        event.target.style.display = 'none';
        event.target.parentElement.parentElement.querySelector('.avatar-placeholder').style.display = 'flex';
      }
    },

  };
  </script>
  
  <style scoped>
* {
  box-sizing: border-box;
}

.user-info-container {
  min-height: 100vh;
  background: rgba(60, 194, 255, 1);
  padding: 24px 24px 48px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.user-info-container::before {
  content: '';
  position: absolute;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: rgba(59, 194, 255, 0.15);
  top: -250px;
  right: -250px;
  animation: float 20s ease-in-out infinite;
  pointer-events: none;
}

.user-info-container::after {
  content: '';
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: rgba(60, 194, 255, 0.2);
  bottom: -200px;
  left: -200px;
  animation: float 15s ease-in-out infinite reverse;
  pointer-events: none;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  50% { transform: translate(30px, -30px) rotate(180deg); }
}

.user-info-wrapper {
  width: 100%;
  max-width: 900px;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  position: relative;
  z-index: 1;
}

/* Header Section */
.user-info-header {
  background: rgba(60, 194, 255, 1);
  padding: 24px 40px;
  color: white;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.avatar-section {
  display: flex;
  align-items: center;
  gap: 24px;
}

.avatar-wrapper {
  position: relative;
}

.user-avatar {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  border: 4px solid rgba(255, 255, 255, 0.3);
  object-fit: cover;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.avatar-placeholder {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  border: 4px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.user-info-text {
  flex: 1;
}

.user-name {
  font-size: 32px;
  font-weight: 700;
  margin: 0 0 8px 0;
  color: white;
}

.user-email {
  font-size: 16px;
  margin: 0;
  opacity: 0.9;
  color: white;
}

.page-description {
  font-size: 15px;
  line-height: 1.6;
  margin: 0;
  opacity: 0.95;
  color: white;
}

/* Settings Sections */
.settings-section {
  border-bottom: 1px solid #e5e7eb;
}

.settings-section:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 40px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.section-header:hover {
  background-color: #f9fafb;
}

.section-title-group {
  display: flex;
  align-items: center;
  gap: 16px;
}

.section-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(60, 194, 255, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.section-title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1f2937;
}

.chevron-icon {
  color: #6b7280;
  transition: transform 0.3s ease;
}

.chevron-icon.rotated {
  transform: rotate(180deg);
}

/* Section Content */
.section-content {
  padding: 0 40px 32px 40px;
}

.form-section {
  margin-bottom: 32px;
}

.form-section:last-of-type {
  margin-bottom: 24px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.year-display {
  color: rgba(60, 194, 255, 1);
  font-weight: 700;
}

/* Gender Buttons */
.gender-buttons {
  display: flex;
  gap: 12px;
}

.gender-btn {
  flex: 1;
  padding: 14px 20px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  background: white;
  color: #6b7280;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.gender-btn:hover {
  border-color: #3bc2ff;
  color: #3bc2ff;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 194, 255, 0.2);
}

.gender-btn.active {
  background: rgba(60, 194, 255, 1);
  border-color: #3bc2ff;
  color: white;
  box-shadow: 0 4px 12px rgba(59, 194, 255, 0.3);
}

/* Slider */
.slider-container {
  margin-top: 8px;
}

.year-slider {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e5e7eb;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}

.year-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(60, 194, 255, 1);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(59, 194, 255, 0.4);
  transition: transform 0.2s;
}

.year-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
}

.year-slider::-moz-range-thumb {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: rgba(60, 194, 255, 1);
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 8px rgba(59, 194, 255, 0.4);
}

.slider-labels {
  display: flex;
  justify-content: space-between;
  margin-top: 12px;
  font-size: 12px;
}

.slider-label {
  color: #9ca3af;
  font-weight: 500;
  transition: color 0.2s;
}

.slider-label.active {
  color: rgba(60, 194, 255, 1);
  font-weight: 700;
}

/* Avatar Grid */
.avatar-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 16px;
  margin-top: 8px;
}

.avatar-option {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  border: 3px solid transparent;
  transition: all 0.2s;
}

.avatar-option:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(59, 194, 255, 0.3);
}

.avatar-option.selected {
  border-color: #3bc2ff;
  box-shadow: 0 0 0 4px rgba(59, 194, 255, 0.1);
}

.avatar-option img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-check {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(59, 194, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.avatar-option.selected .avatar-check {
  opacity: 1;
}

/* Form Inputs */
.credentials-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 24px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-input {
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
  color: #1f2937;
}

.form-input:focus {
  outline: none;
  border-color: #3bc2ff;
  box-shadow: 0 0 0 4px rgba(59, 194, 255, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
}

.input-readonly {
  padding: 12px 16px;
  background: #f3f4f6;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 15px;
  color: #6b7280;
  font-weight: 500;
}

.code-input-group {
  display: flex;
  gap: 12px;
}

.code-input-group .form-input {
  flex: 1;
}

.send-code-btn {
  padding: 12px 24px;
  background: rgba(60, 194, 255, 1);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.send-code-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 194, 255, 0.4);
}

/* Save Button */
.save-btn {
  width: 100%;
  padding: 16px;
  background: rgba(60, 194, 255, 1);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 8px;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(59, 194, 255, 0.4);
}

.save-btn:active {
  transform: translateY(0);
}

/* Animations */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  max-height: 2000px;
  overflow: hidden;
}

.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .user-info-container {
    padding: 20px 12px;
  }

  .user-info-header {
    padding: 32px 24px;
  }

  .avatar-section {
    flex-direction: column;
    text-align: center;
  }

  .user-name {
    font-size: 24px;
  }

  .section-header {
    padding: 20px 24px;
  }

  .section-content {
    padding: 0 24px 24px 24px;
  }

  .credentials-grid {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .code-input-group {
    flex-direction: column;
  }

  .send-code-btn {
    width: 100%;
    justify-content: center;
  }

  .avatar-grid {
    grid-template-columns: repeat(auto-fill, minmax(70px, 1fr));
    gap: 12px;
  }

  .avatar-option {
    width: 70px;
    height: 70px;
  }
}

@media (max-width: 480px) {
  .user-info-container {
    padding: 24px 12px 20px 12px;
  }

  .user-info-header {
    padding: 24px 20px;
  }

  .section-header {
    padding: 16px 20px;
  }

  .section-content {
    padding: 0 20px 20px 20px;
  }

  .section-title {
    font-size: 18px;
  }

  .section-icon {
    width: 36px;
    height: 36px;
  }

  /* Fix avatar grid for mobile - make it properly scrollable and touchable */
  .avatar-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    padding: 8px 0;
    width: 100%;
    box-sizing: border-box;
  }

  .avatar-option {
    width: 100%;
    height: 0;
    padding-bottom: 100%; /* Makes it square */
    position: relative;
    border-radius: 12px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  .avatar-option img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }

  .avatar-option .avatar-check {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
  }

  .avatar-option:active {
    transform: scale(0.95);
    opacity: 0.9;
  }

  .avatar-option.selected {
    border-color: #3bc2ff;
    box-shadow: 0 0 0 3px rgba(59, 194, 255, 0.3);
  }

  /* Improve slider touch area */
  .year-slider {
    height: 12px;
    margin: 10px 0;
  }

  .year-slider::-webkit-slider-thumb {
    width: 28px;
    height: 28px;
  }

  .year-slider::-moz-range-thumb {
    width: 28px;
    height: 28px;
  }

  .slider-labels {
    flex-wrap: wrap;
    gap: 4px;
    justify-content: space-between;
  }

  .slider-label {
    font-size: 10px;
    text-align: center;
    flex: 0 0 auto;
    min-width: 50px;
  }

  /* Improve gender buttons for mobile */
  .gender-buttons {
    gap: 10px;
  }

  .gender-btn {
    padding: 12px 16px;
    font-size: 14px;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
  }

  .gender-btn:active {
    transform: scale(0.98);
  }

  /* Better save button touch target */
  .save-btn {
    padding: 14px;
    font-size: 15px;
    min-height: 48px;
    -webkit-tap-highlight-color: transparent;
  }

  .save-btn:active {
    transform: scale(0.98);
    opacity: 0.9;
  }
}
</style>